import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getPdfConfigs } from '../services/api';
import { PDF_REGISTRY, resolvePdfPreferences } from '../pdf/lib/registry';

const PdfConfigContext = createContext(null);

export const PdfConfigProvider = ({ children }) => {
  const { user } = useAuth();
  const [configs, setConfigs] = useState({});  // { [reportType]: { theme, orientation, sections } }
  const [loaded, setLoaded]   = useState(false);

  const load = useCallback(async () => {
    if (!user) { setConfigs({}); setLoaded(true); return; }
    try {
      const res  = await getPdfConfigs();
      const data = res?.data?.data?.configs ?? res?.data?.configs ?? {};
      setConfigs(data);
    } catch {
      setConfigs({});
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  /** Returns resolved { theme, orientation, sections } for a type, merging saved + defaults */
  const resolveConfig = useCallback((type) => {
    return resolvePdfPreferences(configs[type] || null, type);
  }, [configs]);

  /** True if the user has saved explicit settings for this report type */
  const hasSaved = useCallback((type) => !!configs[type], [configs]);

  return (
    <PdfConfigContext.Provider value={{ configs, loaded, reload: load, resolveConfig, hasSaved }}>
      {children}
    </PdfConfigContext.Provider>
  );
};

export const usePdfConfig = () => {
  const ctx = useContext(PdfConfigContext);
  if (!ctx) throw new Error('usePdfConfig must be used within PdfConfigProvider');
  return ctx;
};

/**
 * Resolves pdf config for a type using the registry defaults when no saved config exists.
 * Can also be used outside of react components with raw configs object.
 */
export { resolvePdfPreferences };
export { PDF_REGISTRY };
